import React, { useMemo, useState } from "react";
import { ChevronLeft, X, Bookmark, Camera, Layers, ChevronRight } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { toNumberOrNull, visibleUnitFields, formatFieldValue, fieldLabel } from "@/lib/unitFinder";

/**
 * FloorStackView — Phase 5B. Premium structured fallback for blocks where
 * facade polygon mapping isn't reliable: Etap → Block → vertical floor
 * stack → unit cards. A buyer enters via the "FLOORS" pill in the top bar.
 *
 * Visibility rules:
 *   - Hidden units (entity.visibility === false) never appear.
 *   - Sold units respect `project.viewer_show_sold` (when explicitly false
 *     we filter them out; otherwise they render with the sold style).
 *   - Blocks with `selection_mode === "facade_grid" | "none"` are excluded.
 *   - Blocks with no units that have a numeric `floor` raise an empty
 *     state instead of a broken view.
 */
export default function FloorStackView({
  inventory, project, onClose, onPickUnit, onTrack, lang, defaultLang,
  unitsWithPanorama, shortlistCodes, onToggleShortlist, locate, fieldDefinitions = [],
}) {
  // Phase 5B+ — Restore the last visited block/floor for this project so a
  // sales-person returning to FLOORS lands exactly where they left off. Key
  // is namespaced by project id; sessionStorage keeps it scoped to the
  // current browser tab, which matches a single sales conversation.
  const persistKey = `evb-floorstack-${project?.id || "anon"}`;
  const restored = (() => {
    try {
      const raw = sessionStorage.getItem(persistKey);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? obj : null;
    } catch { return null; }
  })();
  // If Locate handed us a unit at mount, drill straight to its block/floor.
  // Computing this synchronously (from inventory, not eligibleBlocks) avoids
  // a race with the auto-skip / restore-validation effects below.
  const initLocate = (() => {
    if (!locate || (!locate.code && !locate.blockId)) return null;
    let blockG = inventory.groups.find((g) => g.group_type === "block" && g.id === locate.blockId);
    if (!blockG && locate.code) {
      const u = inventory.entities.find((e) => e.code === locate.code);
      if (u) blockG = inventory.groups.find((g) => g.group_type === "block" && g.id === u.group_id);
    }
    if (!blockG) return null;
    return { blockId: blockG.id, etapId: blockG.parent_group_id || "__no_etap__", floor: locate.floor };
  })();

  const [step, setStep] = useState(initLocate ? "stack" : (restored?.step || "etap")); // etap | block | stack
  const [etapId, setEtapId] = useState(initLocate ? initLocate.etapId : (restored?.etapId || null));
  const [blockId, setBlockId] = useState(initLocate ? initLocate.blockId : (restored?.blockId || null));
  const [openFloor, setOpenFloor] = useState(initLocate ? (initLocate.floor != null ? Number(initLocate.floor) : null) : (restored?.openFloor ?? null));
  const [statusFilter, setStatusFilter] = useState(restored?.statusFilter || "all");
  const [pulseCode, setPulseCode] = useState(initLocate ? (locate?.code || null) : null);

  // Persist whenever the buyer moves through the picker.
  React.useEffect(() => {
    try {
      sessionStorage.setItem(persistKey, JSON.stringify({ step, etapId, blockId, openFloor, statusFilter }));
    } catch { /* quota / disabled — fail silent */ }
  }, [persistKey, step, etapId, blockId, openFloor, statusFilter]);

  // Eligible blocks = those with floor_stack/both AND have units carrying a floor.
  const allEntities = useMemo(() => inventory.entities.filter((e) => e.visibility !== false), [inventory.entities]);
  const showSold = project.viewer_show_sold !== false;
  const visibleEntities = showSold ? allEntities : allEntities.filter((e) => e.status !== "sold");

  // `floor` may live either on the dedicated column or in the dynamic
  // `fields.Floor` bag — read both for back-compat with legacy seeds.
  const readFloor = (e) => {
    if (e?.floor != null) return toNumberOrNull(e.floor);
    const f = e?.fields?.Floor ?? e?.fields?.floor;
    if (f == null || f === "") return null;
    const n = toNumberOrNull(f);
    return n;
  };

  const eligibleBlocks = useMemo(() => {
    const out = [];
    for (const g of inventory.groups) {
      if (g.group_type !== "block") continue;
      if (g.visibility === false) continue;
      if (g.selection_mode === "none") continue;
      const blockUnits = visibleEntities.filter((e) => e.group_id === g.id);
      const withFloor = blockUnits
        .map((e) => ({ ...e, _floor: readFloor(e) }))
        .filter((e) => e._floor != null);
      if (g.selection_mode === "floor_stack" || g.selection_mode === "both") {
        out.push({ group: g, blockUnits, withFloor });
      } else if (withFloor.length > 0) {
        out.push({ group: g, blockUnits, withFloor });
      }
    }
    return out;
  }, [inventory.groups, visibleEntities]);

  const etaps = useMemo(() => {
    const ids = new Set(eligibleBlocks.map((b) => b.group.parent_group_id).filter(Boolean));
    return inventory.groups
      .filter((g) => g.group_type === "etap" && g.visibility !== false && ids.has(g.id))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [eligibleBlocks, inventory.groups]);

  const hasOrphanBlocks = useMemo(
    () => eligibleBlocks.some((b) => !b.group.parent_group_id),
    [eligibleBlocks],
  );

  const blocksForEtap = useMemo(() => {
    if (!etapId || etapId === "__no_etap__") return eligibleBlocks;
    return eligibleBlocks.filter((b) => b.group.parent_group_id === etapId);
  }, [etapId, eligibleBlocks]);

  // Locate: when the viewer asks to show a specific unit, auto-drill to its
  // block + floor and pulse the card.
  React.useEffect(() => {
    if (!locate?.ts) return;
    const target = eligibleBlocks.find((b) => b.group.id === locate.blockId)
      || eligibleBlocks.find((b) => b.blockUnits.some((u) => u.code === locate.code));
    if (!target) return;
    setEtapId(target.group.parent_group_id || "__no_etap__");
    setBlockId(target.group.id);
    setStep("stack");
    if (locate.floor != null) setOpenFloor(Number(locate.floor));
    setStatusFilter("all");
    setPulseCode(locate.code);
    const t = setTimeout(() => setPulseCode(null), 5000);
    return () => clearTimeout(t);
  }, [locate?.ts, eligibleBlocks]);

  const activeBlock = blockId ? eligibleBlocks.find((b) => b.group.id === blockId) : null;
  const floors = useMemo(() => {
    if (!activeBlock) return [];
    const byFloor = new Map();
    for (const e of activeBlock.withFloor) {
      const f = Number(e._floor != null ? e._floor : e.floor);
      if (!byFloor.has(f)) byFloor.set(f, []);
      byFloor.get(f).push(e);
    }
    const sorted = [...byFloor.entries()].map(([floor, units]) => {
      units.sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0)
        || String(x.code || "").localeCompare(String(y.code || ""), undefined, { numeric: true }));
      const available = units.filter((u) => u.status === "available").length;
      const reserved = units.filter((u) => u.status === "reserved").length;
      const sold = units.filter((u) => u.status === "sold").length;
      return { floor, units, available, reserved, sold, total: units.length };
    });
    const dir = activeBlock.group.floor_direction || "highest_first";
    sorted.sort((a, b) => dir === "highest_first" ? (b.floor - a.floor) : (a.floor - b.floor));
    return sorted;
  }, [activeBlock]);

  // Auto-open the top floor on first entry into a block.
  React.useEffect(() => {
    if (step === "stack" && floors.length > 0 && openFloor == null) {
      setOpenFloor(floors[0].floor);
    }
  }, [step, floors, openFloor]);

  // Phase 5B+ — Validate restored session state. If the cached blockId
  // points at a block that no longer exists / is no longer eligible, fall
  // back to the block picker. Likewise drop a stale floor selection.
  React.useEffect(() => {
    if (step === "stack" && blockId && !eligibleBlocks.some((b) => b.group.id === blockId)) {
      setStep("block"); setBlockId(null); setOpenFloor(null);
    }
  }, [step, blockId, eligibleBlocks]);
  React.useEffect(() => {
    if (step === "stack" && openFloor != null && floors.length > 0 && !floors.some((f) => f.floor === openFloor)) {
      setOpenFloor(floors[0].floor);
    }
  }, [step, openFloor, floors]);

  // If the project has no Etaps at all, the Etap picker would show just one
  // "No phase grouping" card which feels like a dead step. Auto-skip to the
  // block list in that case (orphan-blocks-only projects).
  React.useEffect(() => {
    if (step === "etap" && etaps.length === 0 && hasOrphanBlocks) {
      setEtapId("__no_etap__");
      setStep("block");
    }
  }, [step, etaps.length, hasOrphanBlocks]);

  const goBack = () => {
    if (step === "stack") { setStep("block"); setBlockId(null); setOpenFloor(null); }
    else if (step === "block") {
      // If there are no real Etaps, the Etap picker is auto-skipped — so
      // going "back" from blocks should close the FLOORS view entirely.
      if (etaps.length === 0) onClose?.();
      else { setStep("etap"); setEtapId(null); }
    }
    else onClose?.();
  };

  // ── Filters ─────────────────────────────────────────────────────────
  const filterUnit = (u) => {
    if (statusFilter !== "all" && u.status !== statusFilter) return false;
    return true;
  };

  return (
    <div className="absolute inset-0 z-30 bg-zinc-950/95 backdrop-blur-md text-white fade-in flex flex-col" data-testid="floor-stack-view">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/10">
        <button onClick={goBack} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.3em] text-white/70 hover:text-white" data-testid="floor-stack-back">
          <ChevronLeft className="h-4 w-4" /> {L(lang, "back")}
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "floors")}</div>
          <div className="font-display text-xl">
            {step === "etap" && (tr(project, "name", lang, defaultLang) || project.name) + " · " + L(lang, "etaps")}
            {step === "block" && (tr(etaps.find((g) => g.id === etapId), "name", lang, defaultLang) || tr(project, "name", lang, defaultLang) || project.name) + " · " + L(lang, "blocks")}
            {step === "stack" && activeBlock && ((tr(activeBlock.group, "name", lang, defaultLang) || activeBlock.group.name) + " · " + L(lang, "floors"))}
          </div>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="floor-stack-close" aria-label={L(lang, "close")}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* Step 1 — Etap picker */}
        {step === "etap" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto" data-testid="floor-stack-etap-picker">
            {hasOrphanBlocks && (
              <button
                onClick={() => { setEtapId("__no_etap__"); setStep("block"); }}
                className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 p-5 text-left transition"
                data-testid="floor-stack-etap-noetap"
              >
                <div className="text-xs uppercase tracking-[0.3em] text-white/55">{L(lang, "all_blocks")}</div>
                <div className="font-display text-lg mt-1">{L(lang, "no_phase_grouping")}</div>
              </button>
            )}
            {etaps.map((g) => {
              const blockCount = eligibleBlocks.filter((b) => b.group.parent_group_id === g.id).length;
              return (
                <button
                  key={g.id}
                  onClick={() => { setEtapId(g.id); setStep("block"); onTrack?.("floor_stack_open", { etap_id: g.id }); }}
                  className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 p-5 text-left transition group"
                  data-testid={`floor-stack-etap-${g.id}`}
                >
                  <div className="text-xs uppercase tracking-[0.3em] text-white/55">{L(lang, "phase")}</div>
                  <div className="font-display text-lg mt-1">{tr(g, "name", lang, defaultLang) || g.name}</div>
                  <div className="mt-2 text-xs text-white/55">{blockCount} {L(lang, "blocks")}</div>
                  <ChevronRight className="h-4 w-4 text-white/40 mt-3 group-hover:translate-x-0.5 transition" />
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2 — Block picker */}
        {step === "block" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto" data-testid="floor-stack-block-picker">
            {blocksForEtap.length === 0 && (
              <div className="col-span-full text-center text-white/55 py-10" data-testid="floor-stack-block-empty">
                {L(lang, "no_floor_blocks")}
              </div>
            )}
            {blocksForEtap.map((b) => (
              <button
                key={b.group.id}
                onClick={() => { setBlockId(b.group.id); setOpenFloor(null); setStep("stack"); onTrack?.("floor_stack_open", { block_id: b.group.id }); }}
                className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 p-5 text-left transition group"
                data-testid={`floor-stack-block-${b.group.id}`}
              >
                <div className="text-xs uppercase tracking-[0.3em] text-white/55">{L(lang, "block")}</div>
                <div className="font-display text-lg mt-1">{tr(b.group, "name", lang, defaultLang) || b.group.name}</div>
                {(() => {
                  const fc = new Set(b.withFloor.map((u) => u._floor)).size;
                  return (
                    <div className="mt-2 text-xs text-white/55">
                      {L(lang, "units_count", b.withFloor.length)} · {fc} {L(lang, "floors")}
                    </div>
                  );
                })()}
                <ChevronRight className="h-4 w-4 text-white/40 mt-3 group-hover:translate-x-0.5 transition" />
              </button>
            ))}
          </div>
        )}

        {/* Step 3 — Floor stack */}
        {step === "stack" && activeBlock && (
          <div className="max-w-5xl mx-auto" data-testid="floor-stack-column">
            {floors.length === 0 ? (
              <div className="text-center text-white/55 py-16" data-testid="floor-stack-empty">
                {L(lang, "no_floor_units")}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
                {/* Building column */}
                <div className="space-y-1.5" data-testid="floor-stack-building">
                  {floors.map((f) => {
                    const active = openFloor === f.floor;
                    const availPct = f.total ? (f.available / f.total) * 100 : 0;
                    const reservedPct = f.total ? (f.reserved / f.total) * 100 : 0;
                    return (
                      <button
                        key={f.floor}
                        onClick={() => { setOpenFloor(f.floor); onTrack?.("floor_select", { block_id: activeBlock.group.id, floor: f.floor }); }}
                        className={`w-full rounded-md transition px-4 py-3 text-left border ${active ? "border-white/40 bg-white/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                        data-testid={`floor-stack-floor-${f.floor}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "floor")}</div>
                            <div className="font-display text-xl leading-tight">{f.floor}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-white/70">{f.available}/{f.total}</div>
                            <div className="text-[10px] uppercase tracking-wider text-white/40">{L(lang, "available")}</div>
                          </div>
                        </div>
                        {/* Availability bar */}
                        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden flex">
                          <div className="bg-emerald-400/80 h-full" style={{ width: `${availPct}%` }} />
                          <div className="bg-amber-400/80 h-full" style={{ width: `${reservedPct}%` }} />
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Units of the open floor */}
                <div data-testid="floor-stack-units">
                  {/* Filters */}
                  <div className="flex items-center gap-2 mb-4 text-xs">
                    <span className="uppercase tracking-[0.3em] text-white/50">{L(lang, "filter")}</span>
                    {["all", "available", "reserved", "sold"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1 rounded-full transition ${statusFilter === s ? "bg-white text-zinc-900" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                        data-testid={`floor-stack-filter-${s}`}
                      >
                        {L(lang, s === "all" ? "all_statuses" : s)}
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const flr = floors.find((f) => f.floor === openFloor);
                    const units = (flr?.units || []).filter(filterUnit);
                    if (!flr) return <div className="text-white/50 text-sm py-10 text-center">{L(lang, "select_floor")}</div>;
                    if (units.length === 0) {
                      return <div className="text-white/50 text-sm py-10 text-center">{L(lang, "no_matches")}</div>;
                    }
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {units.map((u) => (
                          <UnitCard
                            key={u.id}
                            unit={u}
                            project={project}
                            fieldDefinitions={fieldDefinitions}
                            has360={unitsWithPanorama?.has(u.id)}
                            shortlisted={shortlistCodes?.includes(u.code)}
                            pulse={pulseCode === u.code}
                            onView={() => { onPickUnit?.(u.id); onTrack?.("floor_unit_open", { entity_id: u.id, floor: u.floor, source: "floor_stack" }); }}
                            onToggleShortlist={() => onToggleShortlist?.(u.code)}
                            lang={lang}
                            defaultLang={defaultLang}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Unit card ────────────────────────────────────────────────────────────
function UnitCard({ unit, project, fieldDefinitions, has360, shortlisted, pulse, onView, onToggleShortlist, lang, defaultLang }) {
  const cardRows = visibleUnitFields(unit, fieldDefinitions, "card").filter(({ key }) => !["code", "status", "type"].includes(key) && (key !== "price" || project.viewer_show_price !== false));
  const statusStyle = {
    available: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
    reserved:  "bg-amber-400/15 text-amber-200 border-amber-400/30",
    sold:      "bg-red-400/15 text-red-200 border-red-400/30",
  }[unit.status] || "bg-white/10 text-white/70 border-white/15";

  return (
    <div
      className={`rounded-xl border transition p-4 ${pulse ? "border-[#C4A47C] ring-2 ring-[#C4A47C] bg-[#C4A47C]/10 locate-pulse" : "border-white/10 bg-white/5 hover:bg-white/10"} ${unit.status === "sold" ? "opacity-70" : ""}`}
      data-testid={`floor-stack-card-${unit.id}`}
      ref={pulse ? (el) => el && el.scrollIntoView({ behavior: "smooth", block: "center" }) : undefined}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, tr(unit, "type", lang, defaultLang) || unit.type) || tr(unit, "type", lang, defaultLang) || unit.type || L(lang, "unit")}</div>
          <div className="font-display text-lg leading-tight">{unit.code || tr(unit, "name", lang, defaultLang) || L(lang, "unit")}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {has360 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-900/80 border border-white/15" title={L(lang, "tour_360")}>
              <Camera className="h-3 w-3" /> 360
            </span>
          )}
          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${statusStyle}`}>{L(lang, unit.status)}</span>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-y-1 text-xs text-white/70 mb-3">
        {cardRows.map(({ key, fd }) => <React.Fragment key={key}><dt className="text-white/40">{fieldLabel(fd, lang, defaultLang)}</dt><dd className="text-right">{String(formatFieldValue(unit, fd, lang, defaultLang, project) ?? "")}</dd></React.Fragment>)}
      </dl>
      <div className="flex items-center gap-2">
        <button
          onClick={onView}
          className="flex-1 px-3 py-1.5 rounded-md bg-white text-zinc-900 text-xs uppercase tracking-wider font-medium hover:bg-white/90 inline-flex items-center justify-center gap-1.5"
          data-testid={`floor-stack-view-${unit.id}`}
        >
          {L(lang, "open")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onToggleShortlist}
          disabled={!unit.code}
          title={L(lang, shortlisted ? "remove" : "add_to_shortlist")}
          aria-label={L(lang, shortlisted ? "remove" : "add_to_shortlist")}
          className={`px-2.5 py-1.5 rounded-md border text-xs ${shortlisted ? "bg-amber-400/20 border-amber-400/50 text-amber-200" : "border-white/15 hover:bg-white/10 text-white/70"}`}
          data-testid={`floor-stack-shortlist-${unit.id}`}
        >
          <Bookmark className={`h-3.5 w-3.5 ${shortlisted ? "fill-current" : ""}`} />
        </button>
      </div>
    </div>
  );
}
